import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLocalContext } from "../../context/LocalContext";
import {
  FaPrint,
  FaCalculator,
  FaMicrosoft,
  FaShieldAlt,
  FaLaptopCode,
  FaUsersCog,
} from "react-icons/fa";
import "./Hero.css";

const DEFAULT_HERO_IMG =
  "https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=1600&auto=format&fit=crop";

const fade = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08 },
  },
};

export default function Hero() {
  const { webinfo, openTelegram } = useLocalContext();
  const navigate = useNavigate();

  const brand = webinfo?.name || "Your Brand";
  const heroImage = webinfo?.heroImage || DEFAULT_HERO_IMG;

  const services = [
    {
      k: "printer",
      label: "Printer Support",
      icon: <FaPrint />,
      to: "/services/printer",
      desc: "Setup, drivers & troubleshooting",
    },
    {
      k: "acct",
      label: "Accounting & Bookkeeping",
      icon: <FaCalculator />,
      to: "/services/accounting",
      desc: "Monthly books, GST & reports",
    },
    {
      k: "m365",
      label: "Microsoft 365",
      icon: <FaMicrosoft />,
      to: "/services/microsoft",
      desc: "Licenses, email & admin",
    },
    {
      k: "av",
      label: "Antivirus & Security",
      icon: <FaShieldAlt />,
      to: "/services/antivirus",
      desc: "Endpoint setup & renewals",
    },
    {
      k: "web",
      label: "Web Development",
      icon: <FaLaptopCode />,
      to: "/services/web-development",
      desc: "Fast, SEO-ready websites",
    },
    {
      k: "crm",
      label: "CRM Development",
      icon: <FaUsersCog />,
      to: "/services/crm",
      desc: "Pipelines, automation & reports",
    },
  ];

  const stats = [
    { k: "clients", v: webinfo?.stats?.clients || "120+", t: "Businesses supported" },
    { k: "projects", v: webinfo?.stats?.projects || "500+", t: "Projects delivered" },
    { k: "tickets", v: webinfo?.stats?.tickets || "3,000+", t: "Tickets resolved" },
    { k: "sla", v: "24–48h", t: "Typical resolution window" },
  ];

  return (
    <main className="cx">
      {/* === Hero / Masthead === */}
      <section className="cx-hero">
        <div className="wrap">
          <motion.div
            className="cx-hero__grid"
            variants={stagger}
            initial="hidden"
            animate="visible"
          >
            {/* LEFT: Copy */}
            <motion.div className="cx-hero__content" variants={fade}>
              <span className="cx-eyebrow">
                {brand} • IT, Accounting & Web Support
              </span>

              <h1 className="cx-title">
                One team for{" "}
                <em>printers</em>, <em>accounts</em>, <em>security</em> &{" "}
                <em>your website</em>.
              </h1>

              <p className="cx-sub">
                We help you keep devices, accounting tools, Microsoft 365,
                antivirus, websites and CRMs running smoothly — with one simple
                support partner instead of juggling 5 different vendors.
              </p>

              <div className="cx-cta">
                <button
                  className="btn btn--primary"
                  onClick={() => openTelegram?.()}
                >
                  Chat on Telegram
                </button>
                <button
                  className="btn btn--ghost"
                  onClick={() => navigate("/contact")}
                >
                  Get a support plan
                </button>
              </div>

              <ul className="cx-stats" role="list">
                {stats.map((s) => (
                  <li key={s.k}>
                    <b>{s.v}</b>
                    <span>{s.t}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* RIGHT: Visual */}
            <motion.figure className="cx-hero__visual" variants={fade}>
              <div className="cx-hero__image-wrap">
                <img src={heroImage} alt="Team providing support" />
                <div className="cx-hero__ticket">
                  <span className="ticket-pill">Active support</span>
                  <p>“Printer not working after Windows update.”</p>
                  <div className="ticket-meta">
                    <span>Assigned • Tech team</span>
                    <span className="ticket-status">In progress</span>
                  </div>
                </div>
              </div>
            </motion.figure>
          </motion.div>
        </div>
      </section>

      {/* === Services === */}
      <section className="cx-services" aria-labelledby="svcTitle">
        <div className="wrap">
          <header className="svc-head">
            <div>
              <span className="svc-eyebrow">Support areas</span>
              <h2 id="svcTitle" className="cx-h2">
                Everything your business relies on, in one place.
              </h2>
              <p className="cx-lead">
                From your printers and accounting stack to security and web, we
                plug into the tools you already use.
              </p>
            </div>

            <div className="svc-cta">
              <button
                className="btn btn--ghost svc-cta-btn"
                onClick={() => navigate("/services")}
              >
                View all services
              </button>
            </div>
          </header>

          <ul className="svc-grid svc-grid--neo" role="list">
            {services.map((s) => (
              <li key={s.k}>
                <button
                  className="svc-card"
                  onClick={() => navigate(s.to)}
                  aria-label={`${s.label} — ${s.desc}`}
                >
                  <span className="svc-ico" aria-hidden="true">
                    <span className="svc-ico__ring" />
                    <span className="svc-ico__dot" />
                    {s.icon}
                  </span>

                  <div className="svc-main">
                    <strong className="svc-title">{s.label}</strong>
                    <p className="svc-desc">{s.desc}</p>
                  </div>

                  <span className="svc-go" aria-hidden="true">
                    →
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* === Trust strip === */}
      <section className="cx-trust" aria-label="Tools our clients use">
        <div className="wrap">
          <div className="trust">
            <span className="trust__label">We support teams using</span>
            <ul className="trust__logos" role="list">
              <li>Microsoft 365</li>
              <li>QuickBooks</li>
              <li>Tally</li>
              <li>Zoho Books</li>
              <li>HP & Brother</li>
              <li>Epson</li>
            </ul>
          </div>
        </div>
      </section>

      {/* === CTA band === */}
      <section className="cx-band">
        <div className="wrap band">
          <div>
            <h3>Need help with something specific?</h3>
            <p>
              Share your current issue — printer, accounting, login, security or
              website — and we’ll suggest the simplest next step.
            </p>
          </div>
          <div className="band__cta">
            <button
              className="btn btn--primary"
              onClick={() => openTelegram?.()}
            >
              Talk to an expert
            </button>
            <button
              className="btn btn--ghost"
              onClick={() => navigate("/contact")}
            >
              Send a request
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
